import React, { useState } from "react";
import { compose } from "redux";

import withTranslations from "../../../../../hocs/withTranslations";
import ConsentModeHelperWeb from "../../../../../lib/ConsentModeHelper/ConsentModeHelperWeb";
import {
  COOKIE_CONSENT_SECTIONS,
  COOKIE_MODAL_STEP
} from "../../../../../lib/ConsentModeHelper/cookieConsentSections";
import { BUTTON_WITH_ARROW_BACKGROUND_TYPE } from "../../../../../lib/typography/buttonWithArrowConstants";
import { BodyRegular, ButtonWithArrow, H3 } from "../../../common/Typography";
import chevronLeftIcon from "../../../landingPage/pages/Homepage/assets/icons/chevron-left.svg";
import NewModal from "../../NewModal";
import CookieConsentSection from "../components/CookieConsentSection";

const getAcceptAllConsentOptions = () =>
  ConsentModeHelperWeb.consentOptions.reduce((options, key) => {
    options[key] = true;
    return options;
  }, {});

const getRejectConsentOptions = () =>
  ConsentModeHelperWeb.consentOptions.reduce((options, key) => {
    options[key] = ConsentModeHelperWeb.consentEnabledByDefault.includes(key);
    return options;
  }, {});

const transformSettings = settings =>
  Object.keys(settings).reduce((result, key) => {
    result[key] = settings[key] === true ? "granted" : "denied";
    return result;
  }, {});

const COOKIE_MODAL_INTRO_STEP_CONTENT = {
  renderHeader: ({ translate }) => (
    <div className="flex align-center gap-4">
      <H3 tag="h1">{translate("cookie_modal_title")}</H3>
    </div>
  ),
  renderBody: ({ translate }) => (
    <BodyRegular className="opacity-70">
      {translate("cookie_modal_description")}
    </BodyRegular>
  ),
  renderFooter: ({ translate, goToPersonalize, acceptAll, rejectAll }) => (
    <div className="flex flex-col gap-5">
      <ButtonWithArrow
        onClick={goToPersonalize}
        withBackground={false}
        className="w-full"
      >
        {translate("cookie_modal_personalize")}
      </ButtonWithArrow>
      <ButtonWithArrow
        onClick={rejectAll}
        showArrow={false}
        backgroundType={BUTTON_WITH_ARROW_BACKGROUND_TYPE.GRAY}
        className="w-full"
      >
        {translate("cookie_modal_reject")}
      </ButtonWithArrow>
      <ButtonWithArrow onClick={acceptAll} showArrow={false} className="w-full">
        {translate("cookie_modal_accept_all")}
      </ButtonWithArrow>
    </div>
  )
};

const COOKIE_MODAL_PERSONALIZE_STEP_CONTENT = {
  renderHeader: ({ translate, goToIntro }) => (
    <div className="flex align-center gap-4">
      <button
        type="button"
        className="button-5 p-2 flex align-center"
        aria-label="Back"
        onClick={goToIntro}
      >
        <img
          src={chevronLeftIcon}
          alt=""
          width={16}
          height={16}
          className="flex shrink-0 rotate-180"
        />
      </button>
      <H3 tag="h1">{translate("cookie_modal_title")}</H3>
    </div>
  ),
  renderBody: ({ translate, consentOptions, toggleConsentOption }) => (
    <div>
      <BodyRegular className="opacity-70">
        {translate("cookie_modal_description")}
      </BodyRegular>
      <div className="flex flex-col gap-5 mt-6">
        {COOKIE_CONSENT_SECTIONS.map(section => (
          <CookieConsentSection
            key={section.id}
            consentOptions={consentOptions}
            section={section}
            toggleConsentOption={toggleConsentOption}
          />
        ))}
      </div>
    </div>
  ),
  renderFooter: ({ translate, acceptSelected, rejectAll }) => (
    <div className="flex flex-col gap-5">
      <ButtonWithArrow
        onClick={rejectAll}
        showArrow={false}
        backgroundType={BUTTON_WITH_ARROW_BACKGROUND_TYPE.GRAY}
        className="w-full"
      >
        {translate("cookie_modal_reject")}
      </ButtonWithArrow>
      <ButtonWithArrow
        onClick={acceptSelected}
        showArrow={false}
        className="w-full"
      >
        {translate("cookie_modal_accept_selected")}
      </ButtonWithArrow>
    </div>
  )
};

const COOKIE_MODAL_STEP_CONTENT = {
  [COOKIE_MODAL_STEP.INTRO]: COOKIE_MODAL_INTRO_STEP_CONTENT,
  [COOKIE_MODAL_STEP.PERSONALIZE]: COOKIE_MODAL_PERSONALIZE_STEP_CONTENT
};

const CookieModal = ({ hideModal, onClose, onApplySettings, translate }) => {
  const [step, setStep] = useState(COOKIE_MODAL_STEP.INTRO);
  const [consentOptions, setConsentOptions] = useState(
    getAcceptAllConsentOptions
  );

  const stepContent = COOKIE_MODAL_STEP_CONTENT[step];

  const applyConsentAndClose = settings => {
    onApplySettings?.(transformSettings(settings));
    hideModal?.();
  };

  const toggleConsentOption = key => {
    if (ConsentModeHelperWeb.consentEnabledByDefault.includes(key)) {
      return;
    }

    setConsentOptions(current => ({
      ...current,
      [key]: !current[key]
    }));
  };

  const stepProps = {
    translate,
    hideModal,
    goToIntro: () => setStep(COOKIE_MODAL_STEP.INTRO),
    goToPersonalize: () => setStep(COOKIE_MODAL_STEP.PERSONALIZE),
    consentOptions,
    toggleConsentOption,
    acceptAll: () => applyConsentAndClose(getAcceptAllConsentOptions()),
    rejectAll: () => applyConsentAndClose(getRejectConsentOptions()),
    acceptSelected: () => applyConsentAndClose(consentOptions)
  };

  return (
    <NewModal
      hideModal={hideModal}
      onClose={onClose}
      showCloseButton={false}
      maxWidthClassName="max-w-82"
      footer={stepContent.renderFooter(stepProps)}
    >
      {stepContent.renderHeader(stepProps)}
      <div className="mt-4">{stepContent.renderBody(stepProps)}</div>
    </NewModal>
  );
};

export default compose(withTranslations)(CookieModal);
